import React, { memo, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Section from './Section';
import SectionParagraph from './SectionParagraph';
import { AiFillLinkedin, AiFillMail, AiFillPhone } from 'react-icons/ai';
import { useImages } from '../hooks';
import * as css from './WhoWeAre.module.css';

const imagesQuery = graphql`
  query {
    images: allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "team" }
      }
    ) {
      nodes {
        name
        childImageSharp {
          gatsbyImageData
        }
      }
    }
  }
`;

const WhoWeAre = () => {
  const data = useStaticQuery(imagesQuery);
  const images = useImages(data.images.nodes);

  return (
    <Section observe="who-we-are" className={css.root}>
      <SectionParagraph title="Who we are">
        <p>Round.</p>
      </SectionParagraph>
      <div className={css.graphic}>
        <div className={css.text}>
          <p>/raʊnd/ Shaped like a circle.</p>
          <p>The circle is the symbol of Round. The symbol of us.</p>
          <p>
            It signifies our rounded approach to consulting; the notion that
            true problem-solving involves both a business perspective and a
            human perspective; that top-down and inside-out thinking is mandated
            only by bottom-up and outside-in understanding.
          </p>
          <p>
            It stands for wholeness; the preference for co-creation and
            inclusion over predisposition and exclusion; the choice of a
            customized approach over standardized frameworks.
          </p>
          <p>
            It is how we embrace people with different backgrounds and beliefs
            and make them part of us for a sustainable life, no matter their
            life stage.
          </p>
          <p>
            It is our recognition that everything is circular; what goes around,
            comes around, eventually; by investing in sustainable impact, we
            choose to think “hopefully”.
          </p>
          <p>
            The circle is perpetual movement, like the planets' journey around
            the sun and the great rhythm of the universe. It is a movement with
            no end and no beginning; something that will never grind to a halt,
            never cease to evolve.
          </p>
          <p>Just like us.</p>
          <p>This is why we are Round.</p>
        </div>
        <Person
          name="Amilla"
          image={images.amilla}
          phone="+45 40 23 26 00"
          email="amilla.wurtz@round.dk"
          title="Senior Consultant"
          url="https://dk.linkedin.com/in/amillaw%C3%BCrtz/"
        >
          <p>
            I have 2 years of consulting experience from the agency world, where
            I've had the privilege of working closely with a diverse set of
            clients. My work has ranged from crafting go-to-market strategies
            and value propositions to diving deep into market and customer
            insights. The industries I've touched upon are equally varied,
            including renewable energy, tech, fin-tech, and law. Additionally, I
            have over two years of experience at GreenMobility, a Danish
            car-sharing company, where I focused on strategizing and expanding
            new business operations throughout Europe.
          </p>
          <p>
            On the academic front, I hold a M.Sc. in International Business and
            a B.Sc. in Economics and Business Administration, both from
            Copenhagen Business School.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I'm part of
            building Round for a variety of reasons, all of which converge on a
            single mission: to transform the consulting sector by centering it
            around human values. I'm thrilled to contribute to a company that
            not only leads in its domain but also serves as an agent for
            substantial societal change, extending beyond the industry to affect
            the wider community. Additionally, the team is a significant
            motivator for me. Working alongside a diverse set of experts, who
            are not just proficient in their specializations but also genuinely
            dedicated to enacting positive change, aligns with my own
            aspirations for constant learning, both personally and
            professionally.
          </p>
        </Person>
        <Person
          image={images.andreas}
          name="Andreas"
          email="andreas.wester@round.dk"
          phone="+45 53 53 43 95"
          title="Partner & co-founder"
          url="https://www.linkedin.com/in/andreaswester/"
        >
          <p>
            I spent the last decade in strategy consulting as a partner with ReD
            Associates in New York and with BCG and QVARTZ. I have also worked
            at the Danish Ministry of Finance, in research and as a journalist.
            I co-founded a few companies and an NGO, none of which ever really
            took off in a big way but all of which gave me a keen interest in
            starting a company with great people.
          </p>
          <p>
            I get my professional kicks from teaming up with senior executives
            and their organizations to help solve their most difficult and
            meaningful problems from a combined human and commercial
            perspective. Examples include corporate strategy, customer loyalty
            programs, transformation, and cultural change. I am passionate about
            combining liberal arts and the humanities with traditional strategy
            consulting. I write and give speeches and I am a member of the
            Tech.dk-committee formed by Danish union DJOEF.
          </p>
          <p>
            I somewhat nerdily collect academic degrees. I have an MBA from
            London Business School, an MA in Cultural Studies from Goldsmith’s
            College, London, an MSc and a BA in Political Science from
            University of Copenhagen and a BA Honors in Politics from University
            of Cape Town. And still counting
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to join
            forces with cool people and clients to change the world for the
            better while being a great father, an active participant in my
            community and have time to look at art on the weekends, binge
            Netflix on weekdays, read books and try out all the new sports that
            I am eager to start learning but never patient enough to truly
            master.
          </p>
        </Person>


        <Person
          image={images.bertram}
          name="Bertram"
          email="bertram.thoning@round.dk"
          phone="+45 42 95 01 78"
          title="Senior Consultant"
          url="https://www.linkedin.com/in/bertram-thoning-57652323b/"
        >
          <p>
            I have experience from my work with start-ups in Copenhagen, primarily within the fields of financial accounting and service management. I hold an MSc in Economics from University of Copenhagen.
          </p>
          <p>
            I am passionate about helping clients solve strategic problems in a
            creative and purpose-driven way, combining data-driven results with
            economics to create perspective and insights that secure holistic
            solutions. I am also passionate about working alongside inspiring
            people.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to be part
            of building a consultancy that focuses on sustainable solutions and
            a people-first agenda, helping clients solve business problems in an
            impactful way while changing the world for the better.
          </p>
        </Person>
        <Person
          image={images.christopher}
          name="Christopher"
          email="christopher.uglebjerg@round.dk"
          phone="+45 21 69 93 93"
          title="Consultant"
          url="https://www.linkedin.com/in/christopher-visholm-uglebjerg-6bb447189/"
        >
          <p>
            My professional journey includes three years as a Strategy Analyst in VELUX’ Strategy and M&A department, where I developed strong analytical skills and a keen eye for detail. In addition, I spent two years organising and running case competitions for the student organisation International Business Case Competition, which allowed me to combine strategic thinking with hands-on leadership experience.
          </p>
          <p>
            Currently, I’m pursuing an MSc in Management of Innovation and Business Development at Copenhagen Business School (CBS), following the completion of my BSc in International Business.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I joined Round because I strongly resonate with its human-first approach to management consulting and its focus on creating sustainable solutions. I was also drawn to Round’s deeply embedded entrepreneurial spirit, as I want to be part of building a company where ownership and co-creation are foundational values.
          </p>
        </Person>

        <Person
          name="Emma"
          image={images.emma}
          phone="+45 71 92 30 73"
          email="emma.revilliod@round.dk"
          title="Senior Consultant"
          url="https://www.linkedin.com/in/emma-revilliod-5a397b135/"
        >
          <p>
            Born and raised in France, I studied in Canada where I graduated
            from a Bachelor in Business Administration at HEC Montreal. During
            my Bachelor I specialized in Applied Economics, and I completed my
            business degree with focus on Political Sciences at McGill
            University. I had the opportunity to use my background in economy
            and politics while working for non-profit organizations in Montreal.
            I was responsible for developing and fostering public and private
            partnerships.
          </p>
          <p>
            I then completed my MSc in Business and Development Studies at
            Copenhagen Business School, and became truly passionate about
            understanding and resolving modern challenges with a human approach.
            Studying development allowed me to explore new perspectives and I
            believe that diversity brings people together and forward, towards a
            better, more sustainable future.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> As a human-first
            and purpose-driven company, Round is challenging the conventional
            idea of consultancy and I wish to take part in this adventure. I
            share Round's purpose to develop innovative business models focusing
            on creating authentic connections and making sustainable impact.
            Being a part of Round represents an opportunity for me to
            participate in setting the agenda for tomorrow while learning from
            great humans.
          </p>
        </Person>
        <Person
          image={images.flemming}
          name="Flemming"
          email="flemming.pedersen@round.dk"
          phone="+45 25 47 29 28"
          title="Partner & co-founder"
          url="https://www.linkedin.com/in/flemmingpedersen/"
        >
          <p>
            I have +18 years of consulting experience from QVARTZ, Bain &
            Company and BCG. In addition, I have 10 years of experience working
            with the Danish Ministry of Finance and the Ministry of Economics
            and Business Affairs. My main passion and expertise is
            purpose-driven strategy development, sustainability and social
            impact, stakeholder management and value creation trough more
            sustainable operating models. My client experiences include
            progressive industry leaders in the private sector, as-well as
            business owning foundations and non-profit organizations.
            Furthermore, I have extensive experience working with the public
            sector.
          </p>
          <p>
            I hold a Master’s in Political Science from Aarhus University and a
            PLD from Harvard Business School. As a part of my continuing
            Executive Education, I have most recently studied “Sustainable
            Business Strategy” at Harvard Business School.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I am super
            motivated by people driven impact, and the purpose of Round is to
            work with and invest in people and businesses, who want to change
            the word for the better – while we prove that it is possible to both
            be a top tier consultant AND have a sustainable life.
          </p>
        </Person>
        <Person
          image={images.frederik}
          name="Frederik H"
          email="frederik.hojland@round.dk"
          phone="+45 29 69 69 35"
          title="Associate Partner"
          url="https://www.linkedin.com/in/frederik-h%C3%B8jland-b999a764/"
        >
          <p>
            I have five years of consulting experience from QVARTZ and Bain &
            Company with primary industry focus on consumer products, tech/SaaS,
            manufacturing, and social impact, working in close collaboration
            with clients on critical topics, incl. corporate and business unit
            strategy, operating model, value management/commercial excellence,
            integration and more.
          </p>
          <p>
            I am very passionate about people, and I strongly believe that
            connecting on a deeper level with colleagues and clients is
            essential to unleashing the full potential of the individual and the
            team.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I joined Round
            after six months of travelling with my girlfriend, having spent
            countless of hours thinking and fantasizing about my future
            workplace, and I did so for three reasons: First, because Round’s
            values and modern approach to building a workplace and collaborating
            with clients, which is rooted in a strong emphasis on sustainability
            and DEI, align with my values and beliefs. Second, I wanted to be
            part of building a company – a place that is top-tier in its field
            without compromising its human-first values. Third, the fact that
            Round is committed to give back to society by investing time and
            money in social impact ventures.
          </p>
        </Person>
        <Person
          image={images['frederik-n']}
          name="Frederik N"
          email="Frederik.norrevig@round.dk"
          phone="+45 24 87 78 40"
          title="Manager"
          url="https://www.linkedin.com/in/frederik-skat-norrevig/"
        >
          <p>
            My career began at CORE & COMPANY, where I focused on organizational
            transformation, strategy, and commercial excellence. Most recently,
            I spent over four years at TDC NET, working as a Strategy Manager on
            corporate strategy, transformation, and M&A, collaborating closely
            with top management and key stakeholders.
          </p>
          <p>
            I have a strong foundation in strategy and transformation, holding a
            Master’s degree in International Business from Copenhagen Business
            School.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I joined Round
            because its human-first approach and focus on meaningful impact
            strongly align with my values. From the start, I was impressed by
            how Round combines a razor-sharp focus on solving complex challenges
            for clients with a culture that prioritizes growth, collaboration,
            and sustainability. I’m excited to contribute to Round’s journey and
            grow alongside inspiring colleagues while making a real difference
            for businesses and society.
          </p>
        </Person>
        <Person
          image={images.jelis}
          name="Jelis"
          email="Jelis.bosse@round.dk"
          phone="+45 29 69 69 49"
          title="Manager"
          url="https://www.linkedin.com/in/jelis-walentin-bosse-9a019871/"
        >
          <p>
            My expertise spans strategic insights, commercial excellence,
            project management, and team leadership. Before joining Round, I was
            Head of Sales & Business Development at the green Danish startup,
            LastObject, where I led strategic initiatives and oversaw B2B sales.
            Prior to that, I honed my consulting skills at Bain & Company and
            QVARTZ, working on projects related to ESG, strategy, and commercial
            due diligence.
          </p>
          <p>
            I hold an MSc in International Business & Politics from Copenhagen
            Business School and bring over six years of experience in management
            consulting.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I was drawn to
            Round’s human-first approach and its commitment to making meaningful
            impacts. The opportunity to be part of a newly established
            consulting firm, building its foundations and shaping its growth
            journey, strongly appealed to me. Combining my problem-solving
            skills with a collaborative and sustainable culture aligns deeply
            with my personal values. I look forward to helping clients tackle
            complex challenges while contributing to impactful initiatives
            alongside talented colleagues.
          </p>
        </Person>
        <Person
          image={images.juliana}
          name="Juliana"
          email="juliana.mee@round.dk"
          phone="+45 36 20 97 94 "
          title="Consultant"
          url="https://www.linkedin.com/in/juliana-mee/"
        >
          <p>
            With a background in grant management, strategic partnerships, and sustainability-driven initiatives, I’ve built my career around fostering social impact through collaboration and purpose-led work. I previously worked as a Grant Specialist at the Novo Nordisk Foundation, where I managed a broad, sustainability-focused portfolio, led large-scale programmes, and streamlined internal processes. Before that, I was part of the Museum for the United Nations – UN Live, where I focused on cultivating partnerships and fundraising. Most recently, I took a deliberate career break to travel and reflect, which ultimately deepened my commitment to work that is both meaningful and value-driven.  
          </p>
          <p>
            I also hold a BSc in Sociology from the University of Bristol and a Master’s degree in International Development Studies from the University of Amsterdam. 
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> Round stood out to me not just for what they do, but how they do it, driven by a bold commitment to setting a new standard in the industry through a human-centred, socially sustainable approach. I was inspired by Round’s courage to challenge the norm, which aligns with my own values and the kind of impact I aspire to make in the world. I'm thrilled to join the team, and to have the opportunity to work and grow alongside such inspiring and purpose-driven colleagues. 
          </p>
        </Person>
        <Person
          image={images.kai}
          name="Kai"
          email="kai.zhang@round.dk"
          phone="+45 93 40 96 76"
          title="Senior Consultant"
          url="https://www.linkedin.com/in/kai-zhang-6303b1157/"
        >
          <p>
            With a background in Mathematics and Economics from the University
            of Copenhagen, and an MSc in Advanced Economics and Finance from
            CBS, I've developed a solid foundation in quantitative methods and
            data analysis. My professional journey has taken me through various
            roles in both the financial sector and risk management, including
            positions at Danmark’s National bank and McKinsey. Outside of work,
            I'm passionate about engaging in formal debate, alternative economic
            theories and expanding my intellectual curiosity.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> Becoming a part of
            Round means embracing my whole self and combining all the quirky
            interests I have into a well-ROUNDed life. Round’s human-first
            approach and desire to include democratic ownership resonate
            strongly with my personal principles and priorities. From the first
            conversation, I felt a strong alignment between my personal desires
            and Round's values. I am excited to develop alongside Round and
            explore how my personal interests can enrich my professional life.
          </p>
        </Person>
                <Person
          image={images.kasper}
          name="Kasper"
          email="Kasper.kveiborg@round.dk"
          phone="+45 22 33 35 53"
          title="Senior Consultant"
          url="https://www.linkedin.com/in/kasper-kveiborg-223a5a1/"
        >
          <p>
            My professional journey includes time in top-tier management consulting at QVARTZ, where I developed a strong foundation in strategy and problem-solving. Additionally, I spent seven years at Nordea, most recently as Head of Management Office in Nordea Investment Banking & Equities, serving as a trusted advisor to senior executives across the Nordic organisation.
          </p>

          <p>
            I hold a quantitative educational background and bring extensive experience working with senior stakeholders in large organisations.
          </p>

          <p>
            <strong>Why am I part of building Round?</strong> What attracted me to Round was the combination of rigorous consulting and a strong focus on the people behind business challenges. Throughout my career, I’ve seen that the best results come from balancing business objectives with an understanding of the individuals involved. I’m excited to bring my experience to Round, learn from talented colleagues, and help create value for clients.
          </p>
        </Person>
        <Person
          image={images.kshitij}
          name="Kshitij"
          email="Kshitij.aggarwal@round.dk"
          phone="+45 91 11 95 01"
          title="Junior Consultant"
          url="https://www.linkedin.com/in/kshitij-aggarwal-ku/"
        >
          <p>
            My professional journey includes a business analyst role at Landmark
            Group, where I sharpened my analytical skills in the retail
            industry. Additionally, I’ve served as Head of Finance at the Danish
            Debate Association, a role that allowed me to balance strategy with
            leadership.
          </p>
          <p>
            Currently I’m pursuing my Master’s in Advanced Economics and Finance at CBS.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> During the
            interview process, I was impressed by Round’s innovative and
            holistic approach to management consulting. What stood out to me was
            its ability to leverage advancements in social data science,
            creating greater value for clients and redefining how challenges are
            addressed. It quickly became clear that this was the right place for
            me. I’m absolutely thrilled about the opportunity to collaborate
            with talented and inspiring colleagues, learn about different
            industries, and contribute to meaningful projects.
          </p>
        </Person>
        <Person
          image={images.malthe}
          name="Malthe"
          email="malthe.andersen@round.dk"
          phone="+45 28 68 14 05"
          title="Junior Consultant"
          url="https://www.linkedin.com/in/malthe-bell-andersen-494170138/"
        >
          <p>
            I enjoy working with complex issues that mandate not only a business
            perspective but also an insight on societal impact. Problem-solving
            is where I find my drive, especially when I am able to provide
            creative solutions that originate from thinking outside the box.
          </p>
          <p>
            I am currently studying for my MSc in Political Science at University of Copenhagen.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> Round offers a
            unique opportunity in two ways. Firstly, being part of a growing
            company that is still being shaped provides a dynamic and exciting
            experience. Secondly, I believe that incorporating sustainability
            into the DNA of a business is the future of the private sector. I
            also think that the impact the private sector has on society will
            only increase. Therefore, I believe that the quintessential goal for
            companies is to see sustainability as a condition for commercial
            excellence and not merely a bonus.
          </p>
        </Person>
        <Person
          image={images.bina}
          name="Jacobina"
          email="jacobina.hvidt@round.dk"
          phone="+45 93 10 99 91"
          title="Marketing Manager"
          url="https://www.linkedin.com/in/jacobina-hvidt-351892170/"
        >
          <p>
            I have +5 years of experience in marketing departments at Canon EMEA
            and QVARTZ, where I worked with various forms of copy, digital
            design and branding. I love utilizing my creative mindset and
            transforming ideas into engaging and inspiring content. I have a BA
            in English and an MA in English and Communication from The
            University of Copenhagen. The latter of which I completed during my
            exchange in Thailand, where I wrote my thesis on sustainable
            marketing.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I had been
            following Round’s journey on the sidelines and found myself
            continuously inspired by their goals and integrity. Their
            human-first approach and focus on sustainability are values that I
            strongly believe in, and I am thrilled to be in a company that is
            taking active steps towards positively impacting our society.
          </p>
        </Person>
        <Person
          image={images.liva}
          name="Liva"
          email="liva.merrild@round.dk"
          phone="+45 22 91 28 04"
          title="Consultant"
          url="https://www.linkedin.com/in/liva-helbo-merrild-035483151/"
        >
          <p>
            Most recently I was at Junior Consult, where I spent nearly three
            years as both a consultant and member of management. During my time
            there, I worked with various clients on external projects and took
            on key responsibilities within the organisation, including
            overseeing organizational tasks and long-term strategy development.
          </p>
          <p>
            Recently, I completed my Master's in Political Science at Aarhus
            University, further strengthening my analytical and strategic
            skills.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I wanted to join
            Round because I resonate with its values and human-first approach. I
            was especially intrigued by Round’s commitment to creating socially
            sustainable impact, both within the company and through their work,
            offering the opportunity to make a difference for both businesses
            and society. I am truly excited to be part of Round and contribute
            to its journey.
          </p>
        </Person>
        <Person
          image={images.martin}
          name="Martin"
          email="martin.j.mortensen@round.dk"
          phone="+45 29 68 69 40"
          title="Partner & co-founder"
          url="https://www.linkedin.com/in/martinjaquetmortensen/"
        >
          <p>
            I have +13 years of consulting experience from QVARTZ and Bain &
            Company. Before consulting, I also worked in the public sector. I’m
            grounded in corporate / sector strategy and strategy execution, M&A
            / commercial due diligence (sell- and buy-side) and operating model
            transformation with additional experience in e.g. commercial
            excellence and efficiency analysis. I’ve worked with both Public and
            private sector clients (primarily Nordic mid-cap) across industries
            such as PE, energy, financial services, and transportation.
          </p>
          <p>
            I hold a M.Sc. in Political Science from Aarhus University and
            completed part of it at the Danish Embassy to the United Nations in
            New York.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to work
            loyal to the premise that value creation and sustainable change are
            only realized when stellar analysis goes hand in hand with a
            people-first agenda – for both our clients, ourselves, and the
            society at large.
          </p>
        </Person>
        <Person
          image={images.mathilde}
          name="Mathilde"
          email="mathilde.sonne@round.dk"
          phone="+45 27 51 10 18"
          title="Senior Consultant"
          url="https://www.linkedin.com/in/mathilde-sonne/"
        >
          <p>
          I have +3 years of experience at the Danish Ministry of Finance, where I managed numerous complex economic and political cases across high-priority agendas, including the social, health and employment sectors. I am deeply motivated by work that creates value not only for businesses, but for society at large.  I began my journey in consultancy at Junior Consult, where I spent nearly three years developing my problem-solving skills. Since then, I have worked as a Statistician at the OECD, and I have taught macroeconomics at both Aspiri A/S and Aarhus BSS. Collectively, these experiences have shaped how I think about impact – grounded in analysis, but always with people in mind.  
          </p>
          <p>
          I hold both my Bachelor’s and Master’s degrees in Economics from Aarhus University, and I’m excited to bring that perspective into Round’s ambitious, purpose-driven environment. 
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> Round’s holistic, human-first approach to management consulting and its commitment to creating socially sustainable impact strongly appeal to me. Moreover, Round’s ambition to foster a sustainable, people-first culture for its employees closely aligns with my personal values. I’m thrilled to be part of Round, working alongside talented and inspiring colleagues to create meaningful value for both businesses and society.
          </p>
        </Person>
        <Person
          image={images.morten}
          name="Morten"
          email="morten.andreassen@round.dk"
          phone="+45 29 68 69 49"
          title="Partner & co-founder"
          url="https://www.linkedin.com/in/mortenandreassen/"
        >
          <p>
            I have +13 years of consulting experience from QVARTZ and Bain &
            Company. As part of my time in QVARTZ, I did 6 months of exchange at
            Port Jackson Partners, a Sydney based tier 1 consulting company. In
            addition, I have 2 years of M&A experience from Clearwater
            International.I am grounded in corporate strategy, people, and
            organization with additional experience in e.g., commercial due
            diligence and operating model transformation. I have global
            experience from working in 25+ countries and industry experience
            from market leading companies within FMCG, energy and manufacturing.
          </p>
          <p>
            I hold a Master’s in finance from Aarhus University and completed
            part of it at the London School of Economics.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to build a
            consulting company with strong people-oriented values and a focus on
            creating a positive impact on our planet – and create a place where
            we dare to challenge and redefine strategic thinking and how we work
            with our clients.
          </p>
        </Person>
        <Person
          name="Oliver"
          image={images.oliver}
          phone="+45 31 18 41 48"
          email="oliver.langhoff@round.dk"
          title="Manager"
          url="https://www.linkedin.com/in/oliver-quorning-langhoff/"
        >
          <p>
            I have close to 2 years of experience from Investment Banking at ABG
            Sundal Collier, helping clients across different industries drive
            forward their strategic agenda through M&A transactions and IPOs.
            Prior to this, I spent 1+ year managing illiquid investments across
            equity, debt, and infrastructure projects for Danske Bank and Danica
            Pension.
          </p>
          <p>
            From my prior experiences I have found that what really excites me
            is working with clients towards finding solutions to challenges that
            are central to the business. I enjoy being in an environment that
            encourages experimentation and out-of-the-box thinking while working
            on business-critical issues in close collaboration with clients and
            colleagues.
          </p>
          <p>
            I hold a MSc. in Finance and Investments from Copenhagen Business
            School and I have done an exchange semester at the University of
            Michigan.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I have joined
            Round, first and foremost, because of the people. I am highly
            motivated by working alongside people from different backgrounds and
            I want to be challenged in my own way of thinking. I believe that
            there is a great potential to deliver sustainable solutions to
            clients by driving meaningful collaboration through a people-first
            culture.
          </p>
        </Person>
        <Person
          image={images.pia}
          name="Pia"
          email="pia.moller@round.dk"
          phone="+45 51 29 33 79"
          title="Partner & Head of People Support"
          url="https://www.linkedin.com/in/pia-lindhage-m%C3%B8ller-2b7a212/"
        >
          <p>
            I spent the last almost 16 years in consulting as team leader in
            QVARTZ and Bain & Company supporting my colleagues on a daily basis.
            In addition, I have 10+ years of experience working in the travel
            industry as a travel consultant.
          </p>
          <p>
            I am passionate about making the wheels turn smoothly, but I get my
            energy from the people I work closely with, from helping and
            supporting them. I am extremely interested in how we grow and
            develop as a team and equally eager to take part in this
            development/growth.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to be part
            of something meaningful and to build a company driven by a
            people-first culture. I believe that being part of Round will give
            me the best starting point to give my best - every day! And also, I
            want to be part of a company that focuses on sustainability - not
            only for the people in Round, but also for the society.
          </p>
        </Person>
        <Person
          image={images.rasmusg}
          name="Rasmus G."
          email="rasmus.gravergaard@round.dk"
          phone="+45 25 54 02 52"
          title="Consultant"
          url="https://www.linkedin.com/in/rasmus-gravergaard-737027221/"
        >
          <p>
            I previously worked at Lundbeckfonden, where I focused primarily on
            projects centered around sustainability and strategy. I am also a
            member of the CBS International Case Teams and have represented CBS
            internationally at several Case Competitions.
          </p>
          <p>
            I am currently pursuing a MSc in Management of Innovation and Business Development at CBS and is currently on exchange at Universitá Bicconi.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> As a business
            student, strategy consulting has always interested me due to its
            demand for responsibility coupled with the potential for a great and
            lasting impact. Round instantly appealed to me as a space where
            strategies are crafted with a positive impact in mind. Moreover, as
            a student, the smaller size of Round stood out to me, giving me the
            opportunity to collaborate closely with highly experienced
            professionals allowing for a steep learning curve.
          </p>
        </Person>
        <Person
          image={images.rasmus}
          name="Rasmus S."
          email="rasmus.sorensen@round.dk"
          phone="+45 24 29 39 39"
          title="Partner & co-founder"
          url="https://www.linkedin.com/in/rasmussoerensen/"
        >
          <p>
            I have had the pleasure of spending +13 years in consulting,
            primarily with QVARTZ and subsequently with Bain & Company. Prior to
            consulting, I did an 11-year stint in advertising with leading
            advertising agencies like Wibroe, Duckert & Partners/PeopleGroup,
            Grey, and DDB. I am truly passionate about working in the nexus
            between strategy, company culture, and value creation – to fuel
            transformation and strengthen market positioning. My experience
            include work for market leaders across the Nordics within
            manufacturing, transportation, retail, and energy.
          </p>
          <p>
            I hold a Master’s in International Management & Marketing from
            Copenhagen Business School and an MBA semester from York University
            – Schulich School of Business, Canada.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I want to build a
            people-first, culture-driven consulting outfit, where experienced
            consultants and shooting stars join forces to create sustainable
            strategies for clients with progressive agendas.
          </p>
        </Person>
        <Person
          image={images.roar}
          name="Roar"
          email="roar.brun.jensen@round.dk"
          phone=""
          title="Senior Advisor"
          url="https://www.linkedin.com/in/roarbrunjensen/"
        >
          <p>
            I have worked as a strategy consultant for ∼30 years, including
            almost 20 as Partner in QVARTZ and Bain&Company. My main focus has
            been on strategy development, organizational design, mergers &
            acquisitions, where I have advised on ∼250 transaction assignments.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> To have fun and
            use my skills to help good people.
          </p>
        </Person>
        <Person
          image={images.sarah}
          name="Sarah"
          email="sarah.hamilton@round.dk"
          phone="+45 31 72 62 10"
          title="Manager"
          url="https://www.linkedin.com/in/sarahhamiltonjakobsen/"
        >
          <p>
            I bring 2 years of consulting experience with Strategy& and an
            additional year in the FMCG industry. During this time, I
            collaborated with a diverse range of clients, engaged in a wide
            spectrum of projects, and created various business cases to
            facilitate better decision-making.
          </p>
          <p>
            I hold an M.Sc. in International Marketing and Management as well as
            a .Sc. in Economics and Business Administration, both from
            Copenhagen Business School.
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> My great
            enthusiasm for being a part of Round's team primarily arises from
            the opportunity to engage within a community that highly values
            integrity. I appreciate how Round challenges conventional paradigms
            of management consulting by incorporating a human perspective into
            strategy development. Additionally, I am drawn to the startup
            culture at Round, where I can play an integral role in shaping and
            influencing the organizational culture.
          </p>
        </Person>
        <Person
          image={images.solvej}
          name="Solvej"
          email="solvej.mikkelsen@round.dk"
          phone="+45 31 46 72 69"
          title="Junior Consultant"
          url="https://www.linkedin.com/in/solvej-s%C3%B8nderskov-mikkelsen-b022852ba/"
        >
          <p>
            I have three years of experience from Beck Bruun, where I developed a sharp eye for detail and at the various analytical aspects when preparing for trials. I recently represented CBS on the global stage at the National Model United Nations conference in New York. I also serve as a President of IBP Union, where I worked to strengthen the study environment in the International Business and Politics programme. 
          </p>
          <p>
            I’m currently pursuing my BSc in International Business and Politics at CBS. 
          </p>
          <p>
            <strong>Why am I part of building Round?</strong> I joined Round because I am motivated by opportunities to combine analytical insight with a human first approach, while being part of a small team of highly competent people who are committed to each other’s growth. 
          </p>
        </Person> 
      </div>
    </Section>
  );
};

const Person = memo(({ image, name, title, email, phone, children, url }) => {
  const [expand, setExpand] = useState(false);
  const els = React.Children.toArray(children);
  const firstPara = els[0];
  const initalText = (
    <p>{firstPara.props.children.substring(0, 300) + '...'}</p>
  );

  return (
    <div className={css.person}>
      <div className={css.image}>
        <GatsbyImage alt={`Profile image of ${name}`} image={image} />
      </div>
      <div className={css.details}>
        <h3>{name}</h3>
        <h4>{title}</h4>
        <div className={css.contact}>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className={css.contactItem}
            >
              <AiFillLinkedin />
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              target="_blank"
              rel="noreferrer"
              className={css.contactItem}
            >
              <AiFillMail />
            </a>
          )}
          {phone && (
            <span className={css.contactItem}>
              <AiFillPhone />
              {phone}
            </span>
          )}
        </div>
        {expand ? children : initalText}
        <button onClick={() => setExpand(!expand)} className={css.readMore}>
          {expand ? 'Close' : 'Read more'}
        </button>
      </div>
    </div>
  );
});

export default memo(WhoWeAre);
